import { useState, useMemo, useCallback, useRef, useEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { JSONContent } from '@tiptap/core';
import type { MapBlock, MapRegion, MapType, ThemeTokens } from '@course-authoring/shared-types';
import { useTheme, resolveLessonThemeColor, getReadableTextColor } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';
import { geoCentroid } from 'd3-geo';
import {
  createRichTextRendererExtensions,
  createRichTextContentResetStyles,
  safeGenerateRichTextHTML,
  safeGenerateNonEmptyRichTextHTML,
  type RichTextRendererExtensions,
} from './richTextRenderer';
import { createContentBlockStyles } from './styles/contentBlocks';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import { InteractionMessage } from './InteractionMessage';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import usStatesTopology from './atlases/us-states-10m.json';
import worldCountriesTopology from './atlases/world-countries-110m.json';
import canadaGeojson from './atlases/canada.json';
import {
  US_FIPS_TO_ABBR_SHORT,
  TERRITORY_LAYOUT,
  matchGeoToRegionKey,
  computeTerritoryPaths,
} from './mapGeoUtils';
import { RichTextContent } from './RichTextContent';

export interface MapRendererProps {
  block: MapBlock;
  onInteractionComplete?: () => void;
  onRegionOpened?: (regionId: string) => void;
  onRegionChanged?: (previousRegionId: string, regionId: string) => void;
  onRegionClosed?: (regionId: string) => void;
  /** Dynamic highlight color for selected/hovered state — typically from lessonThemeColor */
  regionColor?: string;
}

const richTextExtensions = createRichTextRendererExtensions();
const contentResetStyles = createRichTextContentResetStyles('map-region-content', '0.5em');

const modalScrollbarStyles = `
.map-modal-body::-webkit-scrollbar { width: 8px; }
.map-modal-body::-webkit-scrollbar-track { background: transparent; }
.map-modal-body::-webkit-scrollbar-thumb { background-color: rgba(0,0,0,0.3); border-radius: 4px; }
.map-modal-body::-webkit-scrollbar-thumb:hover { background-color: rgba(0,0,0,0.5); }
`;

function createMapTheme(tokens: ThemeTokens) {
  return {
    stateFill: tokens.borders.default,
    pillColor: tokens.brand.primary,
    stroke: tokens.text.onBrand,
    strokeWidth: 0.75,
    labelColor: tokens.text.primary,
    background: 'transparent',
    shadowColor: tokens.brand.primary,
    shadowOpacity: 0.15,
  };
}




const US_TOPOLOGY = usStatesTopology as Record<string, unknown>;
const WORLD_TOPOLOGY = worldCountriesTopology as Record<string, unknown>;
const CA_TOPOLOGY = canadaGeojson as Record<string, unknown>;

const TERRITORY_GEO_OFFSETS: Record<string, [number, number]> = { AS: [10, 0] };
const TERRITORY_INSET_DRAW_ORDER = ['66', '60', '72', '78', '69']; // GU, AS, PR, VI, MP last
const TERRITORY_LABEL_Y = 655;

// Pre-compute territory path data from topology
const territoryPathData = computeTerritoryPaths();

interface MapConfig {
  topology: Record<string, unknown> | string;
  projection: string;
  projectionConfig: { rotate: [number, number, number]; scale: number; center?: [number, number]; parallels?: [number, number] };
  width: number;
  height: number;
}

const MAP_CONFIGS: Record<MapType, MapConfig> = {
  us: {
    topology: US_TOPOLOGY,
    projection: 'geoAlbersUsa',
    projectionConfig: { rotate: [0, 0, 0], scale: 900 },
    width: 800, height: 645,
  },
  canada: {
    topology: CA_TOPOLOGY,
    projection: 'geoConicConformal',
    projectionConfig: { rotate: [92.6, 0, 0], center: [4, 62], parallels: [49, 77], scale: 750 },
    width: 800, height: 600,
  },
  emea: {
    topology: WORLD_TOPOLOGY,
    projection: 'geoMercator',
    projectionConfig: { rotate: [-15, 0, 0], scale: 350, center: [0, 30] },
    width: 800, height: 550,
  },
  apac: {
    topology: WORLD_TOPOLOGY,
    projection: 'geoMercator',
    projectionConfig: { rotate: [-120, 0, 0], scale: 350, center: [0, 10] },
    width: 800, height: 550,
  },
  latam: {
    topology: WORLD_TOPOLOGY,
    projection: 'geoMercator',
    projectionConfig: { rotate: [75, 0, 0], scale: 400, center: [0, -15] },
    width: 800, height: 600,
  },
};


// US territories to exclude from main map (rendered as circle buttons)
const US_TERRITORY_FIPS = new Set(['60', '66', '69', '72', '78']);
// NE small states rendered as circle buttons
const US_NE_SMALL_STATES = new Set(['US-NH', 'US-VT', 'US-MA', 'US-RI', 'US-CT', 'US-NJ', 'US-DE', 'US-MD', 'US-DC']);
// US territories rendered as bottom-row circle buttons
const US_TERRITORY_KEYS = ['US-GU', 'US-MP', 'US-HI', 'US-AS', 'US-PR', 'US-VI'];
// CA small provinces rendered as circle buttons
const CA_SMALL_PROVINCES = new Set(['CA-PE', 'CA-NS']);

// Small countries per world map that are too tiny to see/click on the map
const EMEA_SMALL_COUNTRIES = new Set(['ISR', 'ARE', 'BEL', 'NLD', 'CHE', 'DNK', 'IRL']);
const APAC_SMALL_COUNTRIES = new Set(['SGP', 'HKG', 'TWN', 'NZL']);
const LATAM_SMALL_COUNTRIES = new Set(['CRI', 'PAN', 'SLV', 'DOM']);

const SMALL_COUNTRIES_BY_MAP: Record<string, Set<string>> = {
  emea: EMEA_SMALL_COUNTRIES,
  apac: APAC_SMALL_COUNTRIES,
  latam: LATAM_SMALL_COUNTRIES,
};

// Label offsets for states/provinces where geoCentroid lands off-center
const LABEL_OFFSETS: Record<string, [number, number]> = {
  'US-AK': [0, 1.5], 'US-CA': [-0.5, 0], 'US-FL': [0.75, 0.25],
  'US-LA': [-0.45, 1.25], 'US-MI': [0.5, -1.25], 'US-NY': [0.5, 0],
  'CA-NL': [-2, 1], 'CA-NT': [-1.25, -1.5], 'CA-ON': [-2, 1.5],
};

const ZOOM_CONFIG = { step: 1.5, min: 1, max: 8 };

const MAP_TYPE_PLACEHOLDER_KEYS: Record<MapType, string> = {
  us: 'map.select_state_or_territory',
  canada: 'map.select_province_or_territory',
  emea: 'map.select_country',
  apac: 'map.select_country',
  latam: 'map.select_country',
};

function getRegionAbbr(regionKey: string, mapType: MapType): string {
  if (mapType === 'us') return regionKey.replace('US-', '');
  if (mapType === 'canada') return regionKey.replace('CA-', '');
  return regionKey;
}

function shouldSkipLabel(regionKey: string, mapType: MapType): boolean {
  if (mapType === 'us') {
    return US_NE_SMALL_STATES.has(regionKey) || regionKey === 'US-HI';
  }
  if (mapType === 'canada') {
    return CA_SMALL_PROVINCES.has(regionKey);
  }
  const smallSet = SMALL_COUNTRIES_BY_MAP[mapType];
  if (smallSet && smallSet.has(regionKey)) return true;
  return false;
}

function isUsTerritoryGeo(geo: { id?: string | number }): boolean {
  return geo.id != null && US_TERRITORY_FIPS.has(String(geo.id));
}

function safeParseJson(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'object' && raw !== null) return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return null;
}
interface CircleButtonProps {
  x: number;
  y: number;
  abbr: string;
  isHovered: boolean;
  hasTrait: boolean;
  regionColor?: string;
  onClick: () => void;
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
}

interface CircleButtonInternalProps extends CircleButtonProps {
  mapTheme: ReturnType<typeof createMapTheme>;
  fontFamily: string;
}

function CircleButton({ x, y, abbr, isHovered, hasTrait, regionColor, mapTheme, fontFamily, onClick, onMouseEnter, onMouseLeave }: CircleButtonInternalProps) {
  const useHighlight = isHovered && hasTrait && regionColor;
  return (
    <g
      transform={`translate(${x}, ${y})`}
      style={{ cursor: hasTrait ? 'pointer' : 'default', outline: 'none' }}
      role={hasTrait ? 'button' : undefined}
      tabIndex={hasTrait ? 0 : undefined}
      aria-label={hasTrait ? abbr : undefined}
      onClick={onClick}
      onKeyDown={hasTrait ? (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      onFocus={hasTrait ? (e: React.FocusEvent) => { if (e.currentTarget === e.target) onMouseEnter(e as unknown as React.MouseEvent); } : undefined}
      onBlur={hasTrait ? () => onMouseLeave() : undefined}
      onMouseEnter={onMouseEnter as unknown as (e: unknown) => void}
      onMouseLeave={onMouseLeave}
    >
      <circle r={18} fill={useHighlight ? regionColor : mapTheme.stateFill} stroke="none" />
      <text
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fill: useHighlight && regionColor ? getReadableTextColor(regionColor) : mapTheme.pillColor, fontSize: 16, fontWeight: 700, fontFamily, letterSpacing: '-0.5px', pointerEvents: 'none' }}
      >
        {abbr}
      </text>
    </g>
  );
}

export function MapRenderer({
  block,
  onInteractionComplete,
  onRegionOpened,
  onRegionChanged,
  onRegionClosed,
  regionColor: regionColorProp,
}: MapRendererProps) {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const regionColor = regionColorProp ?? resolveLessonThemeColor(undefined, tokens.lessonColors);
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const styles = useMemo(() => createMapStyles(tokens), [tokens]);
  const baseStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const mapTheme = useMemo(() => createMapTheme(tokens), [tokens]);

  // NA-227/NA-234: same three-tier content-block heading resolution as
  // Likert/Categorization/Single Choice, honoring the authored titleLevel.
  const titleTypography = useMemo(
    () => getContentBlockHeadingTypography(tokens, block.titleLevel ?? 'h2', { isMobile, isSmallMobile }),
    [block.titleLevel, isMobile, isSmallMobile, tokens],
  );
  const titleStyle = useMemo(
    () => ({
      ...baseStyles.h2,
      fontSize: titleTypography.size,
      fontWeight: titleTypography.weight,
      lineHeight: titleTypography.lineHeight,
    }),
    [baseStyles.h2, titleTypography],
  );
  const descriptionStyle = useMemo(
    () => ({
      ...baseStyles.p,
      // NA-205/NA-227 convention: reading prose steps to 16px at ≤576.
      fontSize: isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
    }),
    [baseStyles.p, isSmallMobile, tokens],
  );
  const TitleTag = block.titleLevel ?? 'h2';

  const [selectedRegionKey, setSelectedRegionKey] = useState<string | null>(null);
  const [hoveredRegionKey, setHoveredRegionKey] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tooltip, setTooltip] = useState<{ name: string; x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  // Typeahead
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; startPan: { x: number; y: number } } | null>(null);

  const resolved = block.resolvedMapVersion;
  const regions = resolved?.regions ?? [];
  const mapType = resolved?.mapType ?? 'us';
  const config = MAP_CONFIGS[mapType];

  const regionKeys = useMemo(() => new Set(regions.map((r: MapRegion) => r.regionKey)), [regions]);
  const regionNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    regions.forEach((r: MapRegion) => { m[r.regionKey] = r.name; });
    return m;
  }, [regions]);

  const selectedRegion = useMemo(
    () => regions.find((r: MapRegion) => r.regionKey === selectedRegionKey),
    [regions, selectedRegionKey]
  );

  const filteredRegions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return regions;
    return regions.filter((r: MapRegion) => r.name.toLowerCase().includes(q));
  }, [regions, searchQuery]);

  const regionDescriptionHtml = useMemo(() => {
    if (!selectedRegion) return '';
    const parsed = safeParseJson(selectedRegion.description);
    if (!parsed) return '';
    return safeGenerateRichTextHTML(
      parsed as Parameters<typeof safeGenerateRichTextHTML>[0],
      richTextExtensions as RichTextRendererExtensions
    );
  }, [selectedRegion]);

  const handleRegionSelect = useCallback((regionKey: string) => {
    if (!regionKey) return;
    const nextRegion = regions.find((region: MapRegion) => region.regionKey === regionKey);
    if (!nextRegion) {
      if (!hasInteracted) {
        setHasInteracted(true);
        onInteractionComplete?.();
      }
      return;
    }
    const previousRegion = regions.find((region: MapRegion) => region.regionKey === selectedRegionKey);
    if (showModal && previousRegion && previousRegion.id !== nextRegion.id) {
      onRegionChanged?.(String(previousRegion.id), String(nextRegion.id));
    } else if (!showModal || !previousRegion) {
      onRegionOpened?.(String(nextRegion.id));
    }
    setSelectedRegionKey(regionKey);
    setShowModal(true);
    setSearchQuery('');
    setIsDropdownOpen(false);
    if (!hasInteracted) {
      setHasInteracted(true);
      onInteractionComplete?.();
    }
  }, [
    hasInteracted,
    onInteractionComplete,
    onRegionChanged,
    onRegionOpened,
    regions,
    selectedRegionKey,
    showModal,
  ]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => setZoom((z) => Math.min(z * ZOOM_CONFIG.step, ZOOM_CONFIG.max)), []);
  const handleZoomOut = useCallback(() => {
    setZoom((z) => {
      const next = Math.max(z / ZOOM_CONFIG.step, ZOOM_CONFIG.min);
      if (next === ZOOM_CONFIG.min) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Drag handlers
  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    if (zoom <= ZOOM_CONFIG.min) return;
    dragRef.current = { startX: clientX, startY: clientY, startPan: { ...pan } };
  }, [zoom, pan]);

  const handleDrag = useCallback((clientX: number, clientY: number) => {
    if (!dragRef.current || !mapContainerRef.current) return;
    const rect = mapContainerRef.current.getBoundingClientRect();
    const scale = config.width / rect.width;
    setPan({
      x: dragRef.current.startPan.x + (clientX - dragRef.current.startX) * scale,
      y: dragRef.current.startPan.y + (clientY - dragRef.current.startY) * scale,
    });
  }, [config.width]);

  const handleDragEnd = useCallback(() => { dragRef.current = null; }, []);

  // Tooltip
  const handleGeoMouseEnter = useCallback((regionKey: string, evt: React.MouseEvent) => {
    setHoveredRegionKey(regionKey);
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      setTooltip({ name: regionNameMap[regionKey] ?? '', x: evt.clientX - rect.left, y: evt.clientY - rect.top });
    }
  }, [regionNameMap]);

  const handleMouseMove = useCallback((evt: React.MouseEvent) => {
    if (tooltip && mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      setTooltip((prev) => prev ? { ...prev, x: evt.clientX - rect.left, y: evt.clientY - rect.top } : null);
    }
  }, [tooltip]);

  const handleMouseLeave = useCallback(() => {
    setHoveredRegionKey(null);
    setTooltip(null);
  }, []);

  // Dropdown outside click
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isDropdownOpen]);

  const placeholderText = t(MAP_TYPE_PLACEHOLDER_KEYS[mapType]);
  const isUsMap = mapType === 'us';
  const isCaMap = mapType === 'canada';
  const cx = config.width / 2;
  const cy = config.height / 2;

  // NE small states for US map (circle buttons on right side) - always show
  const neSmallStates = useMemo(() => {
    if (!isUsMap) return [];
    return Array.from(US_NE_SMALL_STATES);
  }, [isUsMap]);

  // US territory buttons (bottom row) — always show if territory path data exists
  const usTerritoryButtons = useMemo(() => {
    if (!isUsMap) return [];
    return US_TERRITORY_KEYS.filter((k) => {
      const abbr = k.replace('US-', '');
      return abbr === 'HI' || !!territoryPathData[abbr];
    });
  }, [isUsMap]);

  // CA small provinces (circle buttons)
  const caSmallProvs = useMemo(() => {
    if (!isCaMap) return [];
    return Array.from(CA_SMALL_PROVINCES);
  }, [isCaMap]);

  // Small countries for world maps (circle buttons on right side)
  const worldSmallCountries = useMemo(() => {
    const smallSet = SMALL_COUNTRIES_BY_MAP[mapType];
    if (!smallSet) return [];
    return Array.from(smallSet).filter((k) => regionKeys.has(k));
  }, [mapType, regionKeys]);

  const handleModalClose = useCallback(() => {
    if (selectedRegion) onRegionClosed?.(String(selectedRegion.id));
    setShowModal(false);
    setSelectedRegionKey(null);
  }, [onRegionClosed, selectedRegion]);

  if (!resolved) {
    return <div style={styles.container}><p style={styles.emptyText}>No map data available.</p></div>;
  }

  const modalElement = showModal && selectedRegion ? createPortal(
    <div
      style={styles.modalOverlay}
      onClick={handleModalClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('map.region_details', { region: selectedRegion.name })}
    >
      <style>{modalScrollbarStyles}</style>
      <div style={styles.modalCenterPane}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h3 style={styles.modalTitle}>{selectedRegion.name}</h3>
            <button
              type="button"
              onClick={handleModalClose}
              style={styles.modalCloseButton}
              aria-label={t('ask_ema.aria_close')}
            >
              &times;
            </button>
          </div>
          {regionDescriptionHtml && (
            <div className="map-modal-body" style={styles.modalBody}>
              <RichTextContent
              className="map-region-content"
              style={styles.regionDescription}
              html={regionDescriptionHtml}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div style={styles.container}>
      <style>{contentResetStyles}</style>

      <div style={isMobile ? styles.gridLayoutMobile : styles.gridLayout}>
        {/* Left column: title, description, region selector */}
        <div style={isMobile ? styles.leftColumnMobile : styles.leftColumn}>
          <div style={styles.header}>
            {block.title && <TitleTag style={titleStyle}>{block.title}</TitleTag>}
            {block.description && (
              typeof block.description === 'string' ? (
                <p style={descriptionStyle}>{block.description}</p>
              ) : (
                (() => {
                  const html = safeGenerateNonEmptyRichTextHTML(block.description as JSONContent, richTextExtensions as RichTextRendererExtensions);
                  return html ? (
                    <RichTextContent
                      className="map-desc-content"
                      style={descriptionStyle}
                      html={html}
                    />
                  ) : null;
                })()
              )
            )}
          </div>

          {/* Typeahead */}
          <div style={styles.controls}>
            <div ref={dropdownRef} style={styles.typeaheadWrapper}>
              <div style={styles.typeaheadInputWrapper}>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                  placeholder={placeholderText}
                  style={styles.typeaheadInput}
                  aria-label={placeholderText}
                  role="combobox"
                  aria-expanded={isDropdownOpen}
                  aria-autocomplete="list"
                />
                <button
                  type="button"
                  onClick={() => {
                  setIsDropdownOpen((p) => !p);
                  inputRef.current?.focus();
                  }}
                  style={styles.typeaheadChevron}
                  aria-label={t('map.toggle_region_list')}
                  tabIndex={-1}
                  >
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              {isDropdownOpen && (
                <ul style={styles.typeaheadList} role="listbox">
                  {filteredRegions.length === 0 ? (
                    <li style={styles.typeaheadNoResults}>{t('map.no_matching_regions')}</li>
                  ) : (
                  filteredRegions.map((r: MapRegion) => (
                    <li key={r.regionKey} role="option" aria-selected={r.regionKey === selectedRegionKey}>
                      <button
                        type="button"
                        onClick={() => handleRegionSelect(r.regionKey)}
                        style={{
                          ...styles.typeaheadOption,
                          backgroundColor:
                          r.regionKey === selectedRegionKey
                          ? tokens.brand.primaryHover
                          : 'transparent',
                          color: 
                          r.regionKey === selectedRegionKey
                          ? tokens.text.onBrand
                          : tokens.text.primary,
                        }}
                      >
                        {r.name}
                      </button>
                    </li>
                  ))
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Right column: Dark gradient SVG Map */}
        <div style={isMobile ? styles.rightColumnMobile : styles.rightColumn}>
          <div
            ref={mapContainerRef}
            style={{ position: 'relative' }}
            onMouseMove={(evt) => { handleMouseMove(evt); if (dragRef.current) handleDrag(evt.clientX, evt.clientY); }}
            onMouseDown={(evt) => { evt.preventDefault(); handleDragStart(evt.clientX, evt.clientY); }}
            onMouseUp={handleDragEnd}
            onMouseLeave={() => { handleMouseLeave(); handleDragEnd(); }}
          >
            <div style={styles.mapContainer}>
              {/* Zoom controls */}
              <div style={styles.zoomControls}>
                <button type="button" style={styles.zoomButton} onClick={handleZoomIn} disabled={zoom >= ZOOM_CONFIG.max} aria-label={t('map.zoom_in')}>+</button>
                <button type="button" style={{ ...styles.zoomButton, borderRight: 'none' }} onClick={handleZoomOut} disabled={zoom <= ZOOM_CONFIG.min} aria-label={t('map.zoom_out')}>&#8722;</button>
              </div>

              <ComposableMap
                projection={config.projection}
                projectionConfig={config.projectionConfig}
                width={config.width}
                height={config.height}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              >
                <defs>
                  <filter id="map-drop-shadow">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor={mapTheme.shadowColor} floodOpacity={mapTheme.shadowOpacity} />
                  </filter>
                </defs>

                {/* Background rect */}
                <rect x={0} y={0} width={config.width} height={config.height} fill="transparent" rx={8} />

                {/* US map position wrapper — shifts main map up to make room for territory insets */}
                <g transform={isUsMap ? 'translate(0, -50)' : undefined}>
                {/* Zoom/pan wrapper */}
                <g transform={`translate(${pan.x}, ${pan.y}) translate(${cx}, ${cy}) scale(${zoom}) translate(${-cx}, ${-cy})`}>
                  <Geographies geography={config.topology} style={{ filter: 'url(#map-drop-shadow)' }}>
                    {({ geographies }) => {
                      const mainGeos = isUsMap ? geographies.filter((g) => !isUsTerritoryGeo(g)) : geographies;
                      return (
                        <>
                          {mainGeos.map((geo) => {
                            const regionKey = matchGeoToRegionKey(geo, mapType, regionKeys);
                            const isClickable = regionKey !== null;
                            const isHovered = regionKey === hoveredRegionKey;

                            return (
                              <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                tabIndex={-1}
                                onClick={() => { if (regionKey) handleRegionSelect(regionKey); }}
                                onMouseEnter={(evt: unknown) => { if (regionKey) handleGeoMouseEnter(regionKey, evt as React.MouseEvent); }}
                                onMouseLeave={handleMouseLeave}
                                style={{
                                  default: {
                                    fill: isHovered && isClickable && regionColor ? regionColor : mapTheme.stateFill,
                                    stroke: mapTheme.stroke,
                                    strokeWidth: mapTheme.strokeWidth,
                                    outline: 'none',
                                    cursor: isClickable ? 'pointer' : 'default',
                                  },
                                  hover: {
                                    fill: isClickable && regionColor ? regionColor : mapTheme.stateFill,
                                    stroke: mapTheme.stroke,
                                    strokeWidth: mapTheme.strokeWidth,
                                    outline: 'none',
                                    cursor: isClickable ? 'pointer' : 'default',
                                  },
                                  pressed: {
                                    fill: isClickable && regionColor ? regionColor : mapTheme.stateFill,
                                    stroke: mapTheme.stroke,
                                    strokeWidth: mapTheme.strokeWidth,
                                    outline: 'none',
                                  },
                                }}
                              />
                            );
                          })}

                          {/* State/Province abbreviation labels */}
                          {mainGeos.map((geo) => {
                            const regionKey = matchGeoToRegionKey(geo, mapType, regionKeys);
                            if (!regionKey) return null;
                            if (shouldSkipLabel(regionKey, mapType)) return null;
                            const centroid = geoCentroid(geo);
                            if (!centroid || (centroid[0] === 0 && centroid[1] === 0)) return null;
                            const offset = LABEL_OFFSETS[regionKey];
                            const coords: [number, number] = offset
                              ? [centroid[0] + offset[0], centroid[1] + offset[1]]
                              : centroid;
                            const abbr = getRegionAbbr(regionKey, mapType);
                            return (
                              <Marker key={`label-${regionKey}`} coordinates={coords}>
                                <text
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  style={{
                                    fill: regionKey === hoveredRegionKey ? getReadableTextColor(regionColor) : mapTheme.labelColor,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    fontFamily: tokens.typography.fontFamily,
                                    fontStyle: 'normal',
                                    pointerEvents: 'none',
                                  }}
                                >
                                  {abbr}
                                </text>
                              </Marker>
                            );
                          })}
                        </>
                      );
                    }}
                  </Geographies>

                  {/* US territory geographic shapes (insets at bottom) */}
                  {isUsMap && TERRITORY_INSET_DRAW_ORDER.map((fips) => {
                    const abbr = US_FIPS_TO_ABBR_SHORT[fips] as string | undefined;
                    if (!abbr) return null;
                    const layout = TERRITORY_LAYOUT[abbr];
                    const pathData = territoryPathData[abbr];
                    if (!pathData || !layout) return null;
                    const isoKey = `US-${abbr}`;
                    const isHovered = isoKey === hoveredRegionKey;
                    const hasTrait = regionKeys.has(isoKey);
                    const paths = Array.isArray(pathData) ? pathData : [pathData];
                    const geoOffset = TERRITORY_GEO_OFFSETS[abbr] ?? [0, 0];
                    const useRegionColor = isHovered && hasTrait && regionColor;
                    return (
                      <g
                        key={`territory-geo-${fips}`}
                        transform={`translate(${layout.x + geoOffset[0]}, ${layout.y + geoOffset[1]})`}
                        style={{
                          cursor: hasTrait ? 'pointer' : 'default',
                        }}
                        onClick={() => { if (hasTrait) handleRegionSelect(isoKey); }}
                        onMouseEnter={(evt: unknown) => { if (hasTrait) handleGeoMouseEnter(isoKey, evt as React.MouseEvent); }}
                        onMouseLeave={handleMouseLeave}
                      >
                        {paths.map((pathD, i) => (
                          <path
                            key={i}
                            d={pathD}
                            fill={useRegionColor ? regionColor : mapTheme.stateFill}
                            stroke="none"
                          />
                        ))}
                      </g>
                    );
                  })}

                  {/* US NE small-state circle buttons (right side) */}
                  {isUsMap && neSmallStates.map((key, i) => {
                    const abbr = getRegionAbbr(key, mapType);
                    const x = 757;
                    const y = 300 + i * 40;
                    return (
                      <CircleButton
                        key={`ne-${key}`}
                        x={x}
                        y={y}
                        abbr={abbr}
                        isHovered={key === hoveredRegionKey}
                        hasTrait={regionKeys.has(key)}
                        regionColor={regionColor}
                        mapTheme={mapTheme}
                        fontFamily={tokens.typography.fontFamily}
                        onClick={() => handleRegionSelect(key)}
                        onMouseEnter={(evt) => handleGeoMouseEnter(key, evt)}
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  })}

                  {/* US territory circle buttons (bottom row) */}
                  {isUsMap && usTerritoryButtons.map((key) => {
                    const abbr = getRegionAbbr(key, mapType);
                    const layout = TERRITORY_LAYOUT[abbr];
                    const x = abbr === 'HI' ? 335 : (layout?.x ?? 0) + (abbr === 'AS' ? -15 : 0);
                    const y = TERRITORY_LABEL_Y;
                    return (
                      <CircleButton
                        key={`terr-${key}`}
                        x={x}
                        y={y}
                        abbr={abbr}
                        isHovered={key === hoveredRegionKey}
                        hasTrait={regionKeys.has(key)}
                        regionColor={regionColor}
                        mapTheme={mapTheme}
                        fontFamily={tokens.typography.fontFamily}
                        onClick={() => handleRegionSelect(key)}
                        onMouseEnter={(evt) => handleGeoMouseEnter(key, evt)}
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  })}

                </g>
                </g>{/* end US map position wrapper */}

                {/* CA small-province circle buttons (right side, fixed position) */}
                {isCaMap && caSmallProvs.length > 0 && (
                  <rect
                    x={config.width - 72}
                    y={config.height - 125}
                    width={54}
                    height={caSmallProvs.length * 42 + 10}
                    rx={6}
                    fill="rgba(255,255,255,0.85)"
                    stroke={tokens.borders.default}
                    strokeWidth={0.5}
                  />
                )}
                {isCaMap && caSmallProvs.map((key, i) => {
                  const abbr = getRegionAbbr(key, mapType);
                  const x = config.width - 45;
                  const y = config.height - 100 + i * 42;
                  return (
                    <CircleButton
                      key={`ca-${key}`}
                      x={x}
                      y={y}
                      abbr={abbr}
                      isHovered={key === hoveredRegionKey}
                      hasTrait={regionKeys.has(key)}
                      regionColor={regionColor}
                      mapTheme={mapTheme}
                      fontFamily={tokens.typography.fontFamily}
                      onClick={() => handleRegionSelect(key)}
                      onMouseEnter={(evt) => handleGeoMouseEnter(key, evt)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}

                {/* World map small-country circle buttons (right side, fixed position) */}
                {worldSmallCountries.length > 0 && (
                  <rect
                    x={config.width - 72}
                    y={55}
                    width={54}
                    height={worldSmallCountries.length * 42 + 10}
                    rx={6}
                    fill="rgba(255,255,255,0.85)"
                    stroke={tokens.borders.default}
                    strokeWidth={0.5}
                  />
                )}
                {worldSmallCountries.length > 0 && worldSmallCountries.map((key, i) => {
                  const abbr = getRegionAbbr(key, mapType);
                  const x = config.width - 45;
                  const y = 80 + i * 42;
                  return (
                    <CircleButton
                      key={`small-${key}`}
                      x={x}
                      y={y}
                      abbr={abbr}
                      isHovered={key === hoveredRegionKey}
                      hasTrait={regionKeys.has(key)}
                      regionColor={regionColor}
                      mapTheme={mapTheme}
                      fontFamily={tokens.typography.fontFamily}
                      onClick={() => handleRegionSelect(key)}
                      onMouseEnter={(evt) => handleGeoMouseEnter(key, evt)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </ComposableMap>
            </div>

            {/* Tooltip */}
            {tooltip && tooltip.name && (
              <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
                {tooltip.name}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Region detail modal — portaled to document.body */}
      {modalElement}

      <InteractionMessage
        variant="boxed"
        // Parent container is a column flex with gap: tokens.spacing.lg (16px),
        // so subtract that from the spec'd 30px to get a 30px visible gap.
        topOffset={30 - 16}
        requireInteraction={block.requireInteraction ?? false}
        isComplete={hasInteracted}
        message={resolved.interactionMessage}
      />
    </div>
  );
}

function createMapStyles(tokens: ThemeTokens): Record<string, CSSProperties> {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      padding: 0,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      width: '100%',
      boxSizing: 'border-box',
    },
    gridLayout: {
      display: 'grid',
      gridTemplateColumns: '1fr 3fr 5fr 1fr',
      gap: tokens.spacing.xl,
      alignItems: 'start',
      width: '100%',
    },
    gridLayoutMobile: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      width: '100%',
    },
    leftColumn: {
      gridColumn: '2 / 3',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
    },
    leftColumnMobile: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
    },
    rightColumn: {
      gridColumn: '3 / 4',
      display: 'flex',
      flexDirection: 'column',
    },
    rightColumnMobile: {
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      marginBottom: 0,
    },
    emptyText: {
      fontSize: '14px',
      color: tokens.text.muted,
      textAlign: 'center',
      padding: tokens.spacing["2xl"],
    },
    mapContainer: {
      position: 'relative',
      width: '100%',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
    zoomControls: {
      position: 'absolute',
      top: '0.5rem',
      left: '0.5rem',
      display: 'flex',
      flexDirection: 'row',
      zIndex: 5,
      backgroundColor: 'rgba(255,255,255,0.85)',
      border: `0.5px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.xs,
      overflow: 'hidden',
    },
    zoomButton: {
      width: '1.3rem',
      height: '1.2rem',
      backgroundColor: 'transparent',
      border: 'none',
      borderRight: `0.5px solid ${tokens.borders.default}`,
      fontSize: '1rem',
      fontWeight: 'bold',
      color: tokens.text.primary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      lineHeight: 1,
    },
    tooltip: {
      position: 'absolute',
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      border: 'none',
      borderRadius: tokens.radius.xs,
      padding: '5px 20px',
      fontSize: '0.65em',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.15)',
      zIndex: 10,
      transform: 'translate(-50%, 8px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    controls: {
      display: 'flex',
      gap: tokens.spacing.md,
      alignItems: 'center',
    },
  // Typeahead dropdown
    typeaheadWrapper: {
      position: 'relative',
      width: '100%',
    },
    typeaheadInputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    typeaheadInput: {
      width: '100%',
      height: '40px',
      padding: `0 ${tokens.spacing["4xl"]} 0 ${tokens.spacing.md}`,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: tokens.typography.fontFamily,
    },
    typeaheadChevron: {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      padding: 0,
    },
    typeaheadList: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      margin: 0,
      padding: 0,
      listStyle: 'none',
      maxHeight: '240px',
      overflowY: 'auto',
      backgroundColor: tokens.backgrounds.page,
      border: `1px solid ${tokens.borders.default}`,
      borderTop: 'none',
      borderRadius: `0 0 ${tokens.radius.sm} ${tokens.radius.sm}`,
      boxShadow: tokens.shadow.md,
      zIndex: 100,
    },
    typeaheadOption: {
      display: 'block',
      width: '100%',
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      border: 'none',
      textAlign: 'left',
      fontSize: '14px',
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      boxSizing: 'border-box',
    },
    typeaheadNoResults: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontSize: '14px',
      color: tokens.text.muted,
    },
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
    },
  // Inner wrapper constrains centering to the content pane width
    modalCenterPane: {
      width: '100%',
      maxWidth: tokens.layout.contentMaxWidth,
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      maxHeight: '100%',
    },
    modalContent: {
      backgroundColor: tokens.backgrounds.page,
      borderRadius: tokens.radius.md,
      width: '90%',
      maxWidth: '600px',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: tokens.shadow.lg,
      fontFamily: tokens.typography.fontFamily,
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${tokens.spacing.lg} ${tokens.spacing.xl}`,
      borderBottom: `1px solid ${tokens.borders.default}`,
      flexShrink: 0,
    },
    modalTitle: {
      margin: 0,
      fontSize: '18px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    modalCloseButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      lineHeight: 1,
      cursor: 'pointer',
      color: tokens.text.muted,
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
    },
    modalBody: {
      flex: 1,
      overflowY: 'scroll',
      padding: `${tokens.spacing.lg} ${tokens.spacing.xl}`,
    },
    regionDescription: {
      fontSize: '14px',
      lineHeight: 1.6,
      color: tokens.text.primary,
    },
  };
}
